import React, { useState, useEffect, useRef } from "react";
import { useLojasInfo } from "@/hooks/useLojas";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, Home, FileText, Plus, X, Calculator, Target, BarChart3, TableProperties, MessageSquareText, Bell, TrendingUp, TrendingDown, AlertCircle, CalendarDays, Trash2, LogOut, Contact, Boxes, Award, Settings, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import OnboardingHelp from "@/components/OnboardingHelp";
import axios from "axios";
import { logDebug, logWarn } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEMA_COR = { grafite: "#0f172a", pedra: "#292524", carvao: "#18181b", marinho: "#1e293b" };

const Layout = ({ onLogout }) => {
  const { empresa } = useLojasInfo();
  const nomeEmpresa = empresa || "Óticas Laurindo";
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificacoes, setNotificacoes] = useState([]);
  const [crmAlertas, setCrmAlertas] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const notifRef = useRef(null);
  const location = useLocation();
  const [temaCor, setTemaCor] = useState(TEMA_COR[localStorage.getItem("tema")] || TEMA_COR.grafite);
  const [features, setFeatures] = useState({});

  useEffect(() => {
    axios.get(`${API}/config/features`)
      .then((r) => setFeatures(r.data?.features || {}))
      .catch((e) => logWarn("Layout: falha ao buscar features, mostrando tudo", e));
  }, []);

  useEffect(() => {
    const aplicarTema = async () => {
      try {
        const r = await axios.get(`${API}/config`);
        const t = r.data?.tema || "grafite";
        localStorage.setItem("tema", t);
        setTemaCor(TEMA_COR[t] || TEMA_COR.grafite);
      } catch (e) { logWarn("Layout: falha ao aplicar tema, mantendo padrão", e); }
    };
    aplicarTema();
    window.addEventListener("tema-updated", aplicarTema);
    return () => window.removeEventListener("tema-updated", aplicarTema);
  }, []);

  const navItems = [
    { path: "/receitas/nova", icon: Plus, label: "Nova Receita", feature: "receitas" },
    { path: "/", icon: Home, label: "Dashboard", feature: "dashboard" },
    { path: "/crm", icon: Contact, label: "CRM Contatos", feature: "crm" },
    { path: "/agenda", icon: CalendarDays, label: "Agenda", feature: "agenda" },
    { path: "/conferencia-caixa", icon: Calculator, label: "Fechamento de Caixa", feature: "alertas-caixa" },
    { path: "/receitas", icon: FileText, label: "Vendas", feature: "receitas" },
    { path: "/metas", icon: Target, label: "Metas", feature: "metas" },
    { path: "/relatorio-lentes", icon: TableProperties, label: "Relatorio Lentes", feature: "lentes" },
    { path: "/estoque", icon: Boxes, label: "Estoque", feature: "estoque" },
    { path: "/premiacao-lentes", icon: Award, label: "Premiação Lentes", feature: "premiacao-lentes" },
    { path: "/catalogo", icon: Images, label: "Catálogo", feature: "catalogo" },
    { path: "/kanban", icon: MessageSquareText, label: "Mensagens", feature: "mensagens" },
    { path: "/executivo-mensal", icon: BarChart3, label: "Executivo Mensal", feature: "executivo-mensal" },
    { path: "/anexos", icon: FileText, label: "Anexos", feature: "anexos" },
    { path: "/configuracoes", icon: Settings, label: "Configurações" },
  ].filter((it) => !it.feature || features[it.feature] !== false);

  useEffect(() => {
    const carregarNotificacoes = async () => {
      try {
        const [resMetas, resCrm] = await Promise.all([
          axios.get(`${API}/notificacoes/metas`),
          axios.get(`${API}/crm/resumo`).catch(() => ({ data: { alertas: [] } })),
        ]);
        setNotificacoes(resMetas.data.notificacoes || []);
        const alertas = resCrm.data.alertas || [];
        setCrmAlertas(alertas);
        setNotifCount((resMetas.data.total || 0) + alertas.length);
      } catch (err) { logDebug("Erro ao carregar notificações:", err); }
    };
    carregarNotificacoes();
    const interval = setInterval(carregarNotificacoes, 60000);
    window.addEventListener("crm-updated", carregarNotificacoes);
    return () => {
      clearInterval(interval);
      window.removeEventListener("crm-updated", carregarNotificacoes);
    };
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  return (
    <div className="flex h-[100dvh] bg-slate-50 overflow-hidden" data-testid="layout-container">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-60 text-slate-50 transform transition-transform duration-200 ease-in-out flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        style={{ backgroundColor: temaCor }}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h1 className="text-base font-bold truncate" data-testid="app-title">{nomeEmpresa}</h1>
          <button className="lg:hidden text-slate-400 hover:text-white" onClick={() => setSidebarOpen(false)} data-testid="close-sidebar-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav dir="rtl" className="px-2 py-1.5 space-y-0.5 flex-1 overflow-y-auto min-h-0 no-scrollbar" data-testid="nav-menu">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} dir="ltr"
                className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md transition-colors ${
                  isActive ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              >
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                <span className="font-medium text-[13px]">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        {/* Logout Button */}
        {onLogout && (
          <div className="p-2 border-t border-white/10">
            <button
              onClick={onLogout}
              className="flex items-center gap-2.5 px-3 py-1.5 w-full rounded-md text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
              data-testid="btn-logout"
            >
              <LogOut className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              <span className="font-medium text-[13px]">Sair</span>
            </button>
          </div>
        )}
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-slate-200 px-6 py-4" data-testid="header">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar-btn">
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h2 className="text-2xl font-semibold text-slate-900">{nomeEmpresa}</h2>
              <p className="text-sm text-slate-500">Sistema de Gestão</p>
            </div>

            {/* Notifications Bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="notifications-bell"
              >
                <Bell className="w-5 h-5 text-slate-600" />
                {notifCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    data-testid="notifications-count">
                    {notifCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
                  data-testid="notifications-panel">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <p className="text-sm font-semibold text-slate-800">Notificacoes</p>
                    <p className="text-xs text-slate-500">{notifCount} alertas</p>
                  </div>
                  {crmAlertas.length > 0 && (
                    <div className="border-b border-slate-100" data-testid="notif-crm-section">
                      <div className="px-4 py-2 bg-amber-50/60">
                        <p className="text-xs font-semibold text-amber-700">CRM — contatos sem retorno (2+ dias)</p>
                      </div>
                      <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                        {crmAlertas.map((a) => (
                          <div key={a.id} className="px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`notif-crm-${a.id}`}>
                            <div className="flex items-start gap-2.5">
                              <div className="mt-0.5 p-1 rounded-full flex-shrink-0 bg-amber-100 text-amber-600">
                                <AlertCircle className="w-3.5 h-3.5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{a.nome || a.telefone}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {a.nome_loja}{a.dias != null ? ` — ${a.dias} dia(s) sem contato` : ""}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Link to="/crm" onClick={() => setNotifOpen(false)}
                        className="block px-4 py-2 text-center text-xs font-medium text-amber-700 hover:bg-amber-50 transition-colors"
                        data-testid="notif-link-crm">
                        Abrir CRM
                      </Link>
                    </div>
                  )}
                  <div className="px-4 py-2 bg-slate-50">
                    <p className="text-xs font-semibold text-slate-600">Metas</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                    {notificacoes.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-slate-400">Nenhuma notificacao</div>
                    ) : (
                      notificacoes.map((n, i) => (
                        <div key={`${n.loja}-${n.tipo}-${i}`} className="px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`notif-item-${i}`}>
                          <div className="flex items-start gap-2.5">
                            <div className={`mt-0.5 p-1 rounded-full flex-shrink-0 ${
                              n.tipo === "meta_atingida" ? "bg-emerald-100 text-emerald-600"
                              : n.tipo === "meta_proxima" ? "bg-amber-100 text-amber-600"
                              : "bg-red-100 text-red-600"
                            }`}>
                              {n.tipo === "meta_atingida" ? <TrendingUp className="w-3.5 h-3.5" />
                                : n.tipo === "meta_proxima" ? <AlertCircle className="w-3.5 h-3.5" />
                                : <TrendingDown className="w-3.5 h-3.5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800">{n.loja}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{n.mensagem}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${
                                    n.progresso >= 100 ? "bg-emerald-500" : n.progresso >= 80 ? "bg-amber-500" : "bg-red-400"
                                  }`} style={{ width: `${Math.min(n.progresso, 100)}%` }} />
                                </div>
                                <span className="text-[10px] font-semibold text-slate-500 flex-shrink-0">{n.progresso}%</span>
                              </div>
                              <p className="text-[10px] text-slate-400 mt-0.5">{fmt(n.vendido)} / {fmt(n.meta)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <Link to="/metas" onClick={() => setNotifOpen(false)}
                    className="block px-4 py-2.5 text-center text-xs font-medium text-blue-600 hover:bg-blue-50 border-t border-slate-200 transition-colors"
                    data-testid="notif-link-metas">
                    Ver todas as metas
                  </Link>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-8 lg:p-10" data-testid="main-content">
          <div className="max-w-full min-w-0 pb-24 md:pb-10">
            <Outlet />
          </div>
        </main>
      </div>

      <OnboardingHelp />
    </div>
  );
};

export default Layout;
